import "./order-tabs.scss";

import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useUpdateEffect } from "react-use";

import OrderCard from "@client/components/order-tabs/order-card/OrderCard";
import { ReactComponent as CustomBorder } from "@client/components/reusable/SvgIcons/library/custom_border.svg";
import { useRootContext } from "@client/context/RootContext";
import useSelectOrders from "@client/hooks/useSelectOrders";
import { ANIMATION_CONSTANTS } from "@client/stores/layout-store";
import { OrderStatuses } from "@client/stores/vendor-store";
import { LABELS } from "@client/utils/labels";

import { NoOrdersBasedOnFilters } from "../no-orders-based-on-filters";
import SvgIcon from "../reusable/SvgIcons";
import { OrderTabSort } from "./order-tab-sort";
import { OrderTabsLoadingOverlay } from "./order-tabs-loading-overlay";

export const OrderTabs = observer(() => {
	const {
		vendorStore: {
			pullingDataInProgress,
			vendorDetails,
			activeOrders,
			activeOrdersTotals,
			activeOrders_search,
			activeOrdersTotals_search,
		},
		layoutStore: {
			sort,
			sort_search,
			setSort,
			showSearchResults,
			isNewOrder,
			setIsNewOrder,
		},
	} = useRootContext();

	const newOrders = (
		showSearchResults ? activeOrders_search : activeOrders
	).filter((order) => order.status === OrderStatuses.Pending);
	const newOrdersTotals = showSearchResults
		? activeOrdersTotals_search.Pending
		: activeOrdersTotals.Pending;

	const pickingOrders = (
		showSearchResults ? activeOrders_search : activeOrders
	).filter((order) => order.status === OrderStatuses.Picking);
	const pickingOrdersTotals = showSearchResults
		? activeOrdersTotals_search.Picking
		: activeOrdersTotals.Picking;

	const packedOrders = (
		showSearchResults ? activeOrders_search : activeOrders
	).filter((order) => order.status === OrderStatuses.Packed);
	const packedOrdersTotals = showSearchResults
		? activeOrdersTotals_search.Packed
		: activeOrdersTotals.Packed;

	const [newOrdersListScroll, setNewOrdersListScroll] = useState(0);
	const [pickingOrdersListScroll, setPickingOrdersListScroll] = useState(0);
	const [packedOrdersListScroll, setPackedOrdersListScroll] = useState(0);
	const [flashNewTabOutline, setFlashNewTabOutline] = useState(false);

	const newOrdersListRef = useRef<HTMLDivElement>(null);
	const pickingOrdersListRef = useRef<HTMLDivElement>(null);
	const packedOrdersListRef = useRef<HTMLDivElement>(null);

	const newOrdersListHeight = newOrdersListRef.current?.scrollHeight - 1;

	const {
		selectedOrders,
		toggleSelectOrder,
		SelectAllOrdersCheckbox,
		SelectedOrdersSummary,
	} = useSelectOrders(packedOrders);

	function onNewOrdersScroll() {
		if (!showSearchResults) {
			setNewOrdersListScroll(Number(newOrdersListRef.current?.scrollTop));
		}
	}

	function onPickingOrdersScroll() {
		if (!showSearchResults) {
			setPickingOrdersListScroll(
				Number(pickingOrdersListRef.current?.scrollTop)
			);
		}
	}

	function onPackedOrdersScroll() {
		if (!showSearchResults) {
			setPackedOrdersListScroll(Number(packedOrdersListRef.current?.scrollTop));
		}
	}

	useUpdateEffect(() => {
		if (!showSearchResults) {
			newOrdersListRef.current?.scrollTo(0, newOrdersListScroll);
			pickingOrdersListRef.current?.scrollTo(0, pickingOrdersListScroll);
			packedOrdersListRef.current?.scrollTo(0, packedOrdersListScroll);
		}
	}, [showSearchResults]);

	useEffect(() => {
		if (!isNewOrder || showSearchResults) {
			return;
		}

		setFlashNewTabOutline(true);
		const timeoutId = window.setTimeout(() => {
			setFlashNewTabOutline(false);
			setIsNewOrder(false);
		}, ANIMATION_CONSTANTS.NEW_ORDER_FLASH_DURATION);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [isNewOrder, showSearchResults]);

	return (
		<>
			{showSearchResults &&
			!newOrders.length &&
			!pickingOrders.length &&
			!packedOrders.length ? (
				<NoOrdersBasedOnFilters
					subtitle="Try changing search query"
					buttonText="Back to all orders"
					isActiveOrders
				/>
			) : (
				<div className="tabs-wrapper">
					<div
						className={classNames("tab-container", "new", {
							"new-order-flash-outline": flashNewTabOutline,
						})}
					>
						<div className="tab-selector">
							<span
								className={classNames("tab-name", {
									fading: pullingDataInProgress,
								})}
							>
								Waiting for acceptance
								{newOrders.length > 0 && (
									<span className="orders-number">{newOrdersTotals}</span>
								)}
							</span>
							<OrderTabSort
								pullingDataInProgress={pullingDataInProgress}
								sort={showSearchResults ? sort_search : sort}
								setSort={setSort}
								columnIndex={0}
							/>
						</div>
						<div
							className="orders-list"
							ref={newOrdersListRef}
							onScroll={onNewOrdersScroll}
						>
							{pullingDataInProgress && (
								<OrderTabsLoadingOverlay customHeight={newOrdersListHeight} />
							)}

							{!newOrders.length ? (
								<div className="no-orders-container">
									<SvgIcon width="400" component={CustomBorder} />
									<div className="no-orders-content">
										<img className="no-orders-img" src={"/images/plane.gif"} />
										<div className="no-orders-text">
											Stay in touch, we will give you an order <br /> as soon as
											it appears
										</div>
									</div>
								</div>
							) : (
								newOrders.map((order) => (
									<OrderCard key={order.id} order={order} />
								))
							)}
						</div>
					</div>

					<div className={classNames("tab-container", "picking")}>
						<div className="tab-selector">
							<span className="tab-name">
								{vendorDetails?.delivery_dates_enabled
									? LABELS.column_pickingAndScheduledDate
									: LABELS.column_picking}

								{pickingOrders.length > 0 && (
									<span className="orders-number">{pickingOrdersTotals}</span>
								)}
							</span>
							<OrderTabSort
								pullingDataInProgress={pullingDataInProgress}
								sort={showSearchResults ? sort_search : sort}
								setSort={setSort}
								columnIndex={1}
							/>
						</div>
						<div
							className="orders-list"
							ref={pickingOrdersListRef}
							onScroll={onPickingOrdersScroll}
						>
							{pickingOrders.map((order) => (
								<OrderCard key={order.id} order={order} isPickingTab />
							))}
						</div>
					</div>
					<div className={classNames("tab-container", "ready")}>
						<div className="tab-selector">
							<span className="tab-name">
								{vendorDetails?.delivery_dates_enabled
									? LABELS.column_readyAndActualDate
									: LABELS.column_ready}
								{packedOrders.length > 0 && (
									<span className="orders-number">{packedOrdersTotals}</span>
								)}
							</span>

							<SelectAllOrdersCheckbox />
						</div>
						<div
							className="orders-list"
							ref={packedOrdersListRef}
							onScroll={onPackedOrdersScroll}
						>
							{packedOrders.map((order) => (
								<OrderCard
									key={order.id}
									order={order}
									isReadyTab
									selected={selectedOrders.includes(order.id)}
									onSelectedChange={toggleSelectOrder}
								/>
							))}
						</div>
					</div>
				</div>
			)}

			<SelectedOrdersSummary />
		</>
	);
});
